import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stethoscope, Video, FileText, Sparkles, LogOut, User, Calendar, BarChart3, Copy, Shield, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import ActivityFeed from '../components/ActivityFeed';
import QuickActions from '../components/QuickActions';
import GlobalSearch from '../components/GlobalSearch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [stats, setStats] = useState({ ideas: 0, scripts: 0 });
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    fetchDoctorData();
    
    // Keyboard shortcuts
    const handleKeyPress = (e) => {
      // Cmd/Ctrl + K for search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      // Cmd/Ctrl + I for ideas
      if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault();
        navigate(`/ideas/${doctorId}`);
      }
      // Cmd/Ctrl + S for script (prevent browser save)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        navigate(`/script/${doctorId}`);
      }
      // Cmd/Ctrl + B for batch
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        navigate(`/batch/${doctorId}`);
      }
      // Cmd/Ctrl + L for library
      if ((e.metaKey || e.ctrlKey) && e.key === 'l') {
        e.preventDefault();
        navigate(`/library/${doctorId}`);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [doctorId, navigate]);

  const fetchDoctorData = async () => {
    try {
      const [doctorRes, ideasRes, scriptsRes] = await Promise.all([
        axios.get(`${API}/doctors/${doctorId}`),
        axios.get(`${API}/ideas/${doctorId}`),
        axios.get(`${API}/scripts/${doctorId}`)
      ]);
      setDoctor(doctorRes.data);
      setStats({
        ideas: ideasRes.data.count || 0,
        scripts: scriptsRes.data.count || 0
      });
    } catch (error) {
      console.error('Error fetching doctor data:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2C5F44] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5C7063]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            <span className="text-2xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Doctor Content Engine
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setSearchOpen(true)}
              variant="outline"
              size="sm"
              className="rounded-full border-[#2C5F44]/20"
              data-testid="dashboard-search-btn"
            >
              <Search className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Search
              <kbd className="ml-2 px-2 py-0.5 text-xs bg-[#F4F1EA] rounded border border-[#2C5F44]/20">⌘K</kbd>
            </Button>
            <QuickActions doctorId={doctorId} />
            <Button
              onClick={() => navigate(`/video/${doctorId}`)}
              size="sm"
              className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
              data-testid="dashboard-nav-studio-btn"
            >
              <Video className="w-4 h-4 mr-2" strokeWidth={1.5} />
              AI Video Studio
            </Button>
            <Button
              onClick={() => navigate(`/services/${doctorId}`)}
              variant="outline"
              size="sm"
              className="rounded-full border-[#D98A6C] text-[#D98A6C] hover:bg-[#D98A6C]/5"
              data-testid="dashboard-nav-services-btn"
              title="Managed Services"
            >
              Done For You
            </Button>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-[#2C5F44]/10">
              <User className="w-4 h-4 text-[#2C5F44]" strokeWidth={1.5} />
              <span className="text-sm text-[#1C2A21] font-medium">{doctor?.name}</span>
            </div>
            <Button
              onClick={() => navigate(`/profile/${doctorId}`)}
              variant="outline"
              size="sm"
              className="rounded-full border-[#2C5F44]/20"
              data-testid="dashboard-profile-btn"
            >
              <User className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <Button
              onClick={() => navigate(`/calendar/${doctorId}`)}
              variant="outline"
              size="sm"
              className="rounded-full border-[#2C5F44]/20"
              data-testid="dashboard-calendar-btn"
            >
              <Calendar className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <Button
              onClick={() => navigate(`/analytics/${doctorId}`)}
              variant="outline"
              size="sm"
              className="rounded-full border-[#2C5F44]/20"
              data-testid="dashboard-analytics-btn"
            >
              <BarChart3 className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="rounded-full border-[#2C5F44]/20"
              data-testid="dashboard-logout-btn"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Welcome back, Dr. {doctor?.name?.split(' ').pop()}!
          </h1>
          <p className="text-lg text-[#5C7063]">
            {doctor?.specialty} | {doctor?.city}, {doctor?.country}
          </p>
        </motion.div>

        {/* PRIMARY CTA: Create Video Without Shooting */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="relative overflow-hidden rounded-3xl p-8 mb-8 cursor-pointer group"
          onClick={() => navigate(`/video/${doctorId}`)}
          data-testid="dashboard-primary-cta"
          style={{
            background: 'linear-gradient(135deg, #2C5F44 0%, #1f4631 50%, #D98A6C 200%)',
          }}
        >
          <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#D98A6C]/20 blur-3xl group-hover:bg-[#D98A6C]/30 transition" />
          <div className="absolute -left-10 bottom-0 w-48 h-48 rounded-full bg-white/5 blur-2xl" />
          <div className="relative flex items-center justify-between flex-wrap gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 text-white text-xs font-semibold rounded-full mb-3 backdrop-blur-sm">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                Primary Action
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Create Video Without Shooting
              </h2>
              <p className="text-white/85 text-base">
                Your AI Video Studio. Upload your face once, then turn any script into a polished,
                compliant video — auto-captions, B-roll, and clinic branding included.
              </p>
            </div>
            <Button
              onClick={(e) => { e.stopPropagation(); navigate(`/video/${doctorId}`); }}
              className="button-pill bg-[#D98A6C] hover:bg-[#c77958] text-white rounded-full px-8 py-6 text-lg shadow-xl shadow-[#D98A6C]/30"
              data-testid="dashboard-create-video-btn"
            >
              <Video className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Open AI Video Studio
            </Button>
          </div>
        </motion.div>

        {/* Urgency banner: posting consistency */}
        {stats.scripts > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-8 flex items-center justify-between gap-4 flex-wrap"
            data-testid="dashboard-urgency-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-800" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-semibold text-amber-900">
                  {stats.scripts} script{stats.scripts === 1 ? '' : 's'} ready · 0 videos posted this week
                </p>
                <p className="text-sm text-amber-700">
                  Consistency builds trust. Generate a video in under a minute.
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate(`/library/${doctorId}`)}
              variant="outline"
              size="sm"
              className="rounded-full border-amber-300 text-amber-900 hover:bg-amber-100 bg-white"
              data-testid="urgency-cta-btn"
            >
              Pick a script →
            </Button>
          </motion.div>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-[#82A08B]/20 rounded-2xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#2C5F44]" strokeWidth={1.5} />
              </div>
              <span className="text-3xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
                {stats.ideas}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-[#1C2A21]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Content Ideas
            </h3>
            <p className="text-sm text-[#5C7063]">AI-generated ideas</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-[#82A08B]/20 rounded-2xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#2C5F44]" strokeWidth={1.5} />
              </div>
              <span className="text-3xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
                {stats.scripts}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-[#1C2A21]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Scripts Created
            </h3>
            <p className="text-sm text-[#5C7063]">Ready to record</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-gradient-to-br from-[#D98A6C] to-[#c77958] rounded-3xl p-8 card-hover text-white"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                <Video className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Ready to Create?
            </h3>
            <p className="text-sm text-white/90">Start with content ideas</p>
          </motion.div>
        </div>

        {/* Action Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10"
          >
            <div className="w-16 h-16 bg-[#82A08B]/20 rounded-2xl flex items-center justify-center mb-6">
              <Sparkles className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-bold text-[#1C2A21] mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Generate Content Ideas
            </h2>
            <p className="text-[#5C7063] mb-6">
              Get 10 AI-powered video content ideas tailored to your specialty and location. 
              Perfect for starting your content journey.
            </p>
            <Button
              onClick={() => navigate(`/ideas/${doctorId}`)}
              className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full w-full"
              data-testid="dashboard-generate-ideas-btn"
            >
              Generate Ideas
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10"
          >
            <div className="w-16 h-16 bg-[#82A08B]/20 rounded-2xl flex items-center justify-center mb-6">
              <FileText className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-bold text-[#1C2A21] mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Create Script
            </h2>
            <p className="text-[#5C7063] mb-6">
              Generate professional, compliant scripts for your video content. 
              Includes hook, explanation, CTA, and disclaimer.
            </p>
            <Button
              onClick={() => navigate(`/script/${doctorId}`)}
              className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full w-full"
              data-testid="dashboard-create-script-btn"
            >
              Create Script
            </Button>
          </motion.div>
        </div>

        {/* Doctor Clone Studio CTA — DEPRECATED, replaced by primary CTA at top */}

        {/* Activity Feed */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.55 }}
          className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10 mb-12"
        >
          <h2 className="text-2xl font-bold text-[#1C2A21] mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Recent Activity
          </h2>
          <ActivityFeed doctorId={doctorId} limit={5} />
        </motion.div>

        {/* Additional Features */}
        <div className="grid md:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-white rounded-3xl p-6 card-hover border border-[#2C5F44]/10 cursor-pointer"
            onClick={() => navigate(`/library/${doctorId}`)}
            data-testid="dashboard-library-card"
          >
            <FileText className="w-8 h-8 text-[#2C5F44] mb-4" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Content Library
            </h3>
            <p className="text-sm text-[#5C7063]">
              Browse and manage all your created scripts
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="bg-white rounded-3xl p-6 card-hover border border-[#2C5F44]/10 cursor-pointer"
            onClick={() => navigate(`/templates/${doctorId}`)}
            data-testid="dashboard-templates-card"
          >
            <Copy className="w-8 h-8 text-[#2C5F44] mb-4" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Templates
            </h3>
            <p className="text-sm text-[#5C7063]">
              Reuse successful script structures
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="bg-white rounded-3xl p-6 card-hover border border-[#2C5F44]/10 cursor-pointer"
            onClick={() => navigate(`/compliance/${doctorId}`)}
            data-testid="dashboard-compliance-card"
          >
            <Shield className="w-8 h-8 text-[#2C5F44] mb-4" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Compliance History
            </h3>
            <p className="text-sm text-[#5C7063]">
              View all compliance reports
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
            className="bg-gradient-to-br from-[#D98A6C] to-[#c77958] rounded-3xl p-6 card-hover cursor-pointer text-white"
            onClick={() => navigate(`/services/${doctorId}`)}
            data-testid="dashboard-services-card"
          >
            <Sparkles className="w-8 h-8 mb-4" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Managed Services
            </h3>
            <p className="text-sm text-white/90">
              Let us handle everything for you
            </p>
          </motion.div>
        </div>
      </div>

      {/* Global Search */}
      <GlobalSearch
        doctorId={doctorId}
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
    </div>
  );
}